// Story 7.12 Iteration 2 (ADO-911), AC3/AC8 + DR-4/DR-6 — the PDP carousel.
//
// This is the PERMANENT HOME the Iteration-1 story left open (D-5 / OQ-3): the
// shipped `RecommendationRail` now mounts on the Product Detail Page, and the
// interim card-hosted `RecommendationsSection` is retired (OQ-12). The carousel
// chrome is prev/next controls plus a scroll-progress thumb — the "See all N"
// link and the pager dots described in the original DR-4 were REMOVED by the
// 2026-07-29 design refresh (`pdp_dropsTheRetiredSeeAllAndPagerDots` guards it).
//
// ONE rail on the PDP:
//   • "Often purchased together" — the FR106 LINKED/related signal. AC8: "The
//     other Related Products block will appear on the PDP." This is the carousel
//     the Figma names.
//
// The FR107 co-purchase signal ("Customers Who Have Bought") was REMOVED from the
// PDP: Story 7.12's AC2 predates the 2026-07-29 design refresh, which shows only
// "Often purchased together" here. With this PDP rail gone, FR107 co-purchase is no
// longer surfaced ANYWHERE in the web app: the upsell modal's cards are LINKED
// products (the FR106 `related` leg, per AC6/AC7 — see UpsellModal.tsx), NOT the
// co-purchase signal. The `customersWhoBought` leg is still fetched, typed and
// stored, but rendered nowhere. That is the intended end state: the PO approved the
// 2026-07-29 PDP redesign, which shows only "Often purchased together" — so FR107
// being unsurfaced follows from an approved design, not an accidental side effect.
// This component renders the `related` leg only (@ADO-911).
//
// AC3 / DR-6 — graceful absence: an empty related list renders NOTHING (no
// heading, no empty slider, no dangling control). That is the LIVE-TODAY path (no
// producer exists for the signal — D-2/D-3), so the section is simply absent; it
// is never an error state and never a broken carousel.
//
// The fetch is the Iteration-1 one, unchanged: ONE lazy per-SKU request through
// `useRecommendationsStore` → `GET /catalogue/products/:sku/recommendations`,
// returning both legs (only the `related` leg is rendered here — see above).
// Availability for the tile SKUs is composed CLIENT-SIDE (7.11 D-10 posture) so
// each tile's Add has its UNAVAILABLE gate data.

import { useEffect, useMemo } from 'react';
import { useRecommendationsStore } from '../../recommendations/useRecommendationsStore';
import { useRecommendationsEntry } from '../../recommendations/useRecommendationsEntry';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useMultibuyDeals } from '../../multibuy/useMultibuyDealsStore';
import { useSessionDepotId } from '../../availability/session-depot';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { RecommendationRail } from '../Browse/RecommendationRail';
import styles from './PdpRecommendations.module.css';

export interface PdpRecommendationsProps {
  readonly productSku: string;
}

export function PdpRecommendations({ productSku }: PdpRecommendationsProps) {
  const load = useRecommendationsStore((s) => s.load);
  // DEPOT-SCOPED read (7-FU-I; @ADO-1222). Two separate things have to be true across a live
  // switch: the effect below must RE-INVOKE `load()` (the store only refetches when asked,
  // and `useHeaderChrome` flips `activeDepot` without resetting the store), and the render
  // must not serve the OLD depot's rails while that refetch is in flight. The subscribed
  // depot does the first; `useRecommendationsEntry` does the second by yielding `undefined`
  // for a depot-mismatched entry, so the section renders nothing until the new depot's
  // rails land rather than offering tiles the user can Add from the wrong depot.
  const entry = useRecommendationsEntry(productSku);
  const sessionDepotId = useSessionDepotId();
  // Enabler E7-C (ADO-2274), D-1: the rails are the selected entry's; an entry change resets the
  // store, and this re-invokes the load for the new one.
  const catalogueEntryKey = useCatalogueEntryKey();

  // The PDP is a single-product page, so — unlike a grid of cards — fetching on
  // mount is correct here: there is exactly one request and the rails are part of
  // the page, not behind a disclosure. The store still guards re-fetches per SKU,
  // and re-invokes on a depot change via `sessionDepotId`.
  useEffect(() => {
    void load(productSku);
  }, [productSku, load, sessionDepotId, catalogueEntryKey]);

  const related = entry?.related ?? [];

  const railSkus = useMemo(() => related.map((p) => p.sku), [related]);
  useAvailability(railSkus);
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(railSkus);
  // Pricing migration 32 (ADO-3985): the same products' multi-buy deals, one request (D-5: no-op
  // unless VITE_REAL_PRICES_ENABLED is on).
  useMultibuyDeals(railSkus);

  // AC3 — nothing to show (still loading, errored, or genuinely empty) renders
  // no section at all. An errored recommendations fetch must not put an error
  // banner on an otherwise healthy product page.
  if (related.length === 0) return null;

  return (
    <div className={styles.recommendations}>
      <RecommendationRail title="Often purchased together" items={related} headingLevel={2} />
    </div>
  );
}
